import React from "react";
import { Box, Stack, type SxProps, type Theme } from "@mui/material";
import {
    StyledCard,
    StyledTypography,
} from "components/Screens/Company/CompanySummary";
import Table from "components/Common/Table/Table";
import type { PillarStatus, ThesisPillar } from "../types";
import { getPillarStatusColor } from "../utils";
import Markdown from "components/Common/Markdown/Markdown";

interface Props {
    thesisPillars: ThesisPillar[];
}

const ThesisPillarsSection: React.FC<Props> = ({ thesisPillars }) => {
    if (!thesisPillars || thesisPillars.length === 0) {
        return null;
    }

    const hasGeneratedFromNotes = thesisPillars.some(
        (pillar) => pillar.pillar_source === "Morning meeting notes"
    );

    const columns: Column[] = [
        {
            id: 1,
            title: hasGeneratedFromNotes
                ? "Thesis Pillars (Generated from notes)"
                : "Thesis Pillars",
            sortable: false,
            value: "investment_pillar",
            cell: (data: string) => (
                <StyledTypography variant="body1">{data}</StyledTypography>
            ),
            show: true,
        },
        {
            id: 2,
            title: "Status",
            sortable: false,
            value: "pillar_status_value",
            cell: (data: string) => {
                const color = thesisPillars.find(
                    (pillar) => pillar.pillar_status_value === data
                )?.pillar_status;
                return (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            height={8}
                            width={8}
                            borderRadius="50%"
                            bgcolor={(theme) =>
                                getPillarStatusColor(color, theme).main
                            }
                            border={"1px solid"}
                            borderColor={(theme) =>
                                getPillarStatusColor(color, theme).main
                            }
                            sx={pulseAnimation(color)}
                        />
                        <StyledTypography variant="body1" whiteSpace="nowrap">
                            {data}
                        </StyledTypography>
                    </Stack>
                );
            },
            show: true,
        },
        {
            id: 3,
            title: "Evidence from Notes",
            sortable: false,
            value: "reasoning_markdown",
            cell: (data: string) => (
                <Markdown
                    text={data}
                    fontSize="16px"
                    lineHeight="1.5"
                    color="black"
                />
            ),
            show: true,
        },
    ];

    return (
        <StyledCard
            sx={{
                "& table": { mb: "0 !important" },
                "& thead > tr": {
                    "& th": { py: 1, fontWeight: "bold" },
                    "&:first-of-type": { display: "none" },
                },
                "& tbody > tr": {
                    "& td": { py: 2 },
                    "&:last-of-type > td": { borderBottom: "none" },
                },
            }}
        >
            <Table
                data={thesisPillars}
                columns={columns}
                filterQuery=""
                pagination={false}
                large
            />
        </StyledCard>
    );
};

export default ThesisPillarsSection;

const pulseAnimation = (color: PillarStatus | undefined): SxProps<Theme> => ({
    animation: `pulse-${color} 2s infinite`,
    [`@keyframes pulse-${color}`]: {
        "0%": {
            boxShadow: (theme) =>
                `0 0 0 0 ${getPillarStatusColor(color, theme).main}51`,
        },
        "70%": {
            boxShadow: (theme) =>
                `0 0 0 2px ${getPillarStatusColor(color, theme).main}51`,
        },
        "100%": {
            boxShadow: (theme) =>
                `0 0 0 0 ${getPillarStatusColor(color, theme).main}51`,
        },
    },
});
